import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
// import { v4 as uuidv4 } from 'uuid';
import { addComputer } from '../../redux/slices/office-slice.js';
import useComputers from '../../hooks/useComputers.js';
import TableTitleHeader from '../common/TableTitleHeader.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import ErrorMessage from '../common/ErrorMessage.jsx';
import ConfirmationModal from '../common/ConfirmationModal.jsx';
import ComputerSettingsDropdown from './ComputerSettingsDropdown.jsx';
import { black, blue, grey } from '../../styles/standard-colors.js';
import CreateUpdateComputerForm from './CreateUpdateComputerForm.jsx';
import getTimeStamp from '../../utils/get-timestamp.js';
import MachineSpecsModal from '../common/MachineSpecsModal.jsx';
/**
 * Component for displaying computer information in a table format.
 *
 * @param {object} props - The props object.
 * @param {object} props.computer - The computer object.
 * @param {string} props.officeSysName - The office system name.
 * @param {string} props.officeId - The office ID.
 * @returns {JSX.Element} JSX element representing the Computer Info Table.
 */
const ComputerInfoTable = ({ computer, officeSysName, officeId }) => {
    const dispatch = useDispatch();
    const [showEdit, setShowEdit] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showDuplicateComputerModal, setShowDuplicateComputerModal] =
        useState(false);
    const [showMachineSpecsModal, setShowMachineSpecsModal] = useState(false);
    const {
        error: computerError,
        loading: computerLoading,
        duplicateComputer
    } = useComputers();

    /**
     * Handler function for duplicating a computer.
     */
    const handleDuplication = async () => {
        setShowDuplicateComputerModal(false);

        const duplicatedComputer = await duplicateComputer(computer._id);

        // Check for failure
        if (!duplicatedComputer) {
            return;
        }

        dispatch(addComputer({ ...duplicatedComputer, credentials: [] }));
    };

    const statusColorMap = {
        ERROR: '#ef4444',
        ACTIVE: '#22c55e',
        WAITING: '#eab308',
        AVAILABLE: '#9ca3af'
    };

    const status = computer.tsPlusLicense?.status;
    const backgroundColor = statusColorMap[status] || '#9ca3af';

    return (
        <div>
            <div className="office-computer-header">
                <TableTitleHeader
                    title={computer.computerSysName}
                    hasSettingsGear={true}
                    settingsGearSize={25}
                    setShowDropdown={setShowDropdown}
                    showDropdown={showDropdown}
                    isRetired={!!computer.retired}
                    isITOnlyConnection={!!computer.isITOnly}
                    hasActiveStateDot={true}
                    underlineColor={blue}
                    customContainerStyles={{ fontSize: '15px' }}
                    computer={computer}
                    includeMachineSpecsButton
                    setShowMachineSpecsModal={setShowMachineSpecsModal}
                />
                {computer.splashtopId && (
                    <MachineSpecsModal
                        show={showMachineSpecsModal}
                        onClose={() => setShowMachineSpecsModal(false)}
                        computerId={computer._id}
                    />
                )}

                {showDropdown && (
                    <ComputerSettingsDropdown
                        computer={computer}
                        showEdit={showEdit}
                        setShowEdit={setShowEdit}
                        showDuplicateComputerModal={showDuplicateComputerModal}
                        setShowDuplicateComputerModal={
                            setShowDuplicateComputerModal
                        }
                        officeSysName={officeSysName}
                    />
                )}
                {computerLoading && <LoadingSpinner />}
                {computerError && (
                    <ErrorMessage message={computerError.message} />
                )}
            </div>
            {showEdit ? (
                <CreateUpdateComputerForm
                    objectToUpdate={computer}
                    setShowEdit={setShowEdit}
                    officeId={officeId}
                    officeSysName={officeSysName}
                />
            ) : (
                <>
                    {computer.retired && (
                        <>
                            <p
                                className="newsc-completed-stamp"
                                style={{
                                    textAlign: 'left'
                                }}>
                                Retired by{' '}
                                {computer.retiredBy
                                    ? computer.retiredBy
                                    : 'Unknown user'}{' '}
                                on{' '}
                                {computer.dateRetired
                                    ? getTimeStamp(computer.dateRetired)
                                    : 'unknown date'}
                            </p>
                            <br />
                            <br />
                        </>
                    )}
                    <table className="office-computer-table">
                        <tbody
                            style={{ color: computer.retired ? grey : black }}>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Computer Systematic Name:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.computerSysName}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Computer Name:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.computerName}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Operating System:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.OS}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    AV Installed?:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.avInstalled ? 'Yes' : 'No'}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Antivirus Name:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.avName}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Domain:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.domain}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Location:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.location}
                                </td>
                            </tr>
                            {/* <tr>
                                <td className="office-computer-table-left-cell">
                                    Syncro Status:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.syncroStatus}
                                </td>
                            </tr> */}
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Install Tech:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.installTech}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Install Date:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.installDate
                                        ? `${
                                              new Date(
                                                  computer.installDate
                                              ).getUTCMonth() + 1
                                          }/${new Date(
                                              computer.installDate
                                          ).getUTCDate()}/${new Date(
                                              computer.installDate
                                          ).getUTCFullYear()}`
                                        : ''}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Linked to Splashtop computer?:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.splashtopId ? 'Yes' : 'No'}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-computer-table-left-cell">
                                    Internet Speed:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '1rem',
                                            flexWrap: 'wrap'
                                        }}>
                                        <div>
                                            Download:{' '}
                                            {computer.internetSpeed?.download !=
                                            null
                                                ? `${computer.internetSpeed.download} Mbps`
                                                : 'N/A'}
                                        </div>
                                        <div>
                                            Upload:{' '}
                                            {computer.internetSpeed?.upload !=
                                            null
                                                ? `${computer.internetSpeed.upload} Mbps`
                                                : 'N/A'}
                                        </div>
                                        <div>
                                            Ping:{' '}
                                            {computer.internetSpeed?.ping !=
                                            null
                                                ? `${computer.internetSpeed.ping} ms`
                                                : 'N/A'}
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td className="office-computer-table-left-cell">
                                    IT only?:
                                </td>
                                <td className="office-computer-table-right-cell">
                                    {computer.isITOnly ? 'Yes' : 'No'}
                                </td>
                            </tr>
                            {computer.tsPlusLicense && (
                                <>
                                    <tr>
                                        <td className="office-computer-table-left-cell">
                                            TSplus License:
                                        </td>
                                        <td className="office-computer-table-right-cell">
                                            {computer.tsPlusLicense.license}
                                            <span
                                                style={{
                                                    marginLeft: '8px',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    backgroundColor:
                                                        computer.tsPlusLicenseType ===
                                                        'MANUAL'
                                                            ? '#f59e0b'
                                                            : '#22c55e',
                                                    color: '#fff'
                                                }}>
                                                {computer.tsPlusLicenseType}
                                            </span>
                                            <span
                                                style={{
                                                    marginLeft: 8,
                                                    padding: '2px 8px',
                                                    borderRadius: 12,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    backgroundColor
                                                }}>
                                                {computer.tsPlusLicense.status}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="office-computer-table-left-cell">
                                            TSplus License # of Users:
                                        </td>
                                        <td className="office-computer-table-right-cell">
                                            {computer.tsPlusLicense.numOfUsers}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="office-computer-table-left-cell">
                                            TSplus License Create Date:
                                        </td>
                                        <td className="office-computer-table-right-cell">
                                            {computer.tsPlusLicense.createDate
                                                ? `${
                                                      new Date(
                                                          computer.tsPlusLicense.createDate
                                                      ).getUTCMonth() + 1
                                                  }/${new Date(
                                                      computer.tsPlusLicense.createDate
                                                  ).getUTCDate()}/${new Date(
                                                      computer.tsPlusLicense.createDate
                                                  ).getUTCFullYear()}`
                                                : ''}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="office-computer-table-left-cell">
                                            TSplus License Rehost Date:
                                        </td>
                                        <td className="office-computer-table-right-cell">
                                            {computer.tsPlusLicense.rehostDate
                                                ? `${
                                                      new Date(
                                                          computer.tsPlusLicense.rehostDate
                                                      ).getUTCMonth() + 1
                                                  }/${new Date(
                                                      computer.tsPlusLicense.rehostDate
                                                  ).getUTCDate()}/${new Date(
                                                      computer.tsPlusLicense.rehostDate
                                                  ).getUTCFullYear()}`
                                                : '—'}
                                        </td>
                                    </tr>
                                </>
                            )}
                            <tr>
                                <td
                                    style={{
                                        borderRight: '1px #DCDCDC solid',
                                        padding: '.5em'
                                    }}>
                                    Note:
                                </td>
                                <td
                                    style={{
                                        padding: '.5em',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                    {computer.note}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            )}
            {showDuplicateComputerModal && (
                <ConfirmationModal
                    title="Duplicate Computer"
                    message="Are you sure you want to duplicate this computer?"
                    onConfirm={handleDuplication}
                    setShowModal={setShowDuplicateComputerModal}
                />
            )}
        </div>
    );
};

ComputerInfoTable.propTypes = {
    computer: PropTypes.object.isRequired,
    officeSysName: PropTypes.string.isRequired,
    officeId: PropTypes.string.isRequired
};

export default ComputerInfoTable;
